import { useMemo, useState } from "react";
import { apiService } from "../../../services/apiService";
import { errorNotify } from "../../../utils/toast";

export const buildPartsFilterPayload = (searchText, searchFilters) => {
  const payload = {};
  if (searchText && String(searchText).trim() !== "") {
    payload.search_text = String(searchText).trim();
  }

  if (searchFilters && typeof searchFilters === "object") {
    Object.assign(payload, searchFilters);
  }

  return payload;
};

const EXPORT_CONFIG = {
  pdf: {
    request: (payload) => apiService.getPartsListingPdf(payload),
    fileName: "parts-listing.pdf",
    fileType: "PDF",
  },
  excel: {
    request: (payload) => apiService.getPartsListingExcel(payload),
    fileName: "parts-listing.xlsx",
    fileType: "Excel",
  },
};

export const usePartsListingExport = (searchText, searchFilters) => {
  const [loadingType, setLoadingType] = useState(null);

  // CRITICAL: Buttons remain disabled until at least one filter or search text is present
  const hasActiveFilters = useMemo(() => {
    const hasSearchText = Boolean(
      searchText && String(searchText).trim().length > 0
    );
    const hasSearchFilters = Boolean(
      searchFilters &&
        typeof searchFilters === "object" &&
        Object.keys(searchFilters).some((key) => {
          const filter = searchFilters[key];
          if (!filter) return false;
          if (Array.isArray(filter.value)) return filter.value.length > 0;
          return (
            filter.value !== undefined &&
            filter.value !== null &&
            filter.value !== ""
          );
        })
    );
    return hasSearchText || hasSearchFilters;
  }, [searchText, searchFilters]);

  const handleExport = async (type) => {
    const config = EXPORT_CONFIG[type];
    if (!config || !hasActiveFilters || loadingType) return;

    setLoadingType(type);
    try {
      const payload = buildPartsFilterPayload(searchText, searchFilters);
      const response = await config.request(payload);
      const responseData = response?.data ?? response;
      const filePath =
        responseData?.url ||
        responseData?.file ||
        (typeof responseData === "string" ? responseData : null);

      if (!filePath) {
        throw new Error(
          `${config.fileType} was generated but no file was returned`
        );
      }

      const fileUrl = /^https?:\/\//i.test(filePath)
        ? filePath
        : `${process.env.REACT_APP_API_FILES_URL}${filePath}`;

      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.setAttribute("download", config.fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      const message = typeof error === "string" ? error : error?.message;
      errorNotify(
        message || `Failed to download parts listing ${config.fileType}`
      );
    } finally {
      setLoadingType(null);
    }
  };

  return {
    hasActiveFilters,
    exportLoading: Boolean(loadingType),
    pdfExportLoading: loadingType === "pdf",
    excelExportLoading: loadingType === "excel",
    handleExportPdf: () => handleExport("pdf"),
    handleExportExcel: () => handleExport("excel"),
  };
};
